import React, { useContext, useEffect, useState } from 'react'
import { toJS } from 'mobx'
import { isEqual } from 'lodash'
import { useQueryClient } from '@tanstack/react-query'
import CatalogCompactWorkItem from '@Components/WorkItemV2/Catalog/CatalogCompactWorkItem'
import { observer } from 'mobx-react'
import { StoresContext } from '@App/contexts'
import SearchPanel from '@Components/Search/SearchPanel'
import useSearchQuery from '@Components/Search/SearchPanel/useSearchQuery'
import useSearchCriteriaUrlSync from '@Components/Search/SearchPanel/useSearchCriteriaUrlSync'
import WorkResultsCardTable from '@Components/WorkItemV2/WorkResultsCardTable'
import CatalogExtendedWorkItem from '@Components/WorkItemV2/Catalog/CatalogExtendedWorkItem'
import { WorkModel } from '@Entities/work/work'
import { CLEAR_WORK_DRAFT } from '@Const/work-registration-forms'
import { WorkSearch } from '@App/stores/search/models/work-search'
import { Works } from '@Entities/work/works'
import { PaginationBody } from '@Entities/pagination'

export const CREATE_WORK_SEARCH_QUERY_KEY = 'create-work-search'

const CreateWorkManualWorkSearch = () => {
    const { createWorkSearchStore, workStore } = useContext(StoresContext)
    const queryClient = useQueryClient()

    // A link can carry search terms (?combinedTitles=…&writerFullNames=…). They must be in the store before the
    // form first renders — it picks its rows up then — so this runs in a state initializer, not an effect.
    const { urlCriteria, syncCriteriaToUrl } = useSearchCriteriaUrlSync(
        createWorkSearchStore.searchCriteriaStore.criteriaOptions
    )
    useState(() => {
        if (urlCriteria) {
            createWorkSearchStore.seedSearchFromUrl(urlCriteria)
            const linkedQuery = createWorkSearchStore.createWorkSearchQuery
            if (Object.keys(linkedQuery).length > 0) {
                workStore.setWorkDraftToLocal({ ...CLEAR_WORK_DRAFT, title: linkedQuery.combinedTitles ?? '' })
            }
        }
    })

    const [hasSearched, setHasSearched] = useState<boolean>(false)

    const query = toJS(createWorkSearchStore.createWorkSearchQuery)
    const pagination: PaginationBody = {
        page: createWorkSearchStore.currentPage,
        limit: createWorkSearchStore.currentResultsPerPageLimit,
        refined: createWorkSearchStore.isRefinedSearch,
    }

    const worksQuery = useSearchQuery<Works>({
        queryKey: [CREATE_WORK_SEARCH_QUERY_KEY, query, pagination],
        queryFn: async () => createWorkSearchStore.fetchWorks(query, pagination),
        // Copied into the store from the query's data, not from the fetch: a cache hit never runs the fetch.
        applyData: (response) => createWorkSearchStore.applySearchResults(response, pagination),
        initialData: createWorkSearchStore.createWorkSearchResults,
        // Reset leaves the query empty; a blank search should not hit the API on its own.
        enabled: hasSearched && Object.keys(query).length > 0,
        onError: (error) => createWorkSearchStore.reportSearchError(error),
    })

    const handleSearch = (submittedSearchCriteria: Partial<WorkSearch>) => {
        const workDraft: WorkModel = { ...CLEAR_WORK_DRAFT, title: submittedSearchCriteria.combinedTitles ?? '' }
        workStore.setWorkDraftToLocal(workDraft)
        syncCriteriaToUrl(submittedSearchCriteria)
        // Clear immediately so a new search never shows the previous criteria's results, whether the
        // new fetch succeeds, fails, or (resubmitting identical criteria below) doesn't fire at all.
        createWorkSearchStore.resetSearchResults()
        // Resubmitting identical criteria on page 1 leaves the query key unchanged, so a key-driven
        // refetch alone wouldn't fire — e.g. retrying after a failed search with the same criteria.
        const keyWillBeUnchanged = isEqual(query, submittedSearchCriteria) && pagination.page === 1

        createWorkSearchStore.setCreateWorkSearchQuery(submittedSearchCriteria)
        createWorkSearchStore.setCurrentPage(1)
        setHasSearched(true)

        if (keyWillBeUnchanged) {
            queryClient.invalidateQueries({ queryKey: [CREATE_WORK_SEARCH_QUERY_KEY] })
        }
    }

    const handleClickCard = () => {
        createWorkSearchStore.setRedirectingToCatalog(true)
    }

    const handleReset = () => {
        syncCriteriaToUrl(undefined)
        createWorkSearchStore.resetWorkRegistrationSearchCriteriaIfNeeded()
        setHasSearched(false)
    }

    useEffect(() => {
        if (Object.keys(createWorkSearchStore.createWorkSearchQuery).length !== 0) {
            setHasSearched(true)
        }
        return () => {
            createWorkSearchStore.resetWorkRegistrationSearchCriteriaIfNeeded()
        }
    }, [])

    return (
        <div className="content-container">
            {createWorkSearchStore.searchCriteriaStore && (
                <SearchPanel
                    searchCriteriaStore={createWorkSearchStore.searchCriteriaStore}
                    onSearch={handleSearch}
                    onReset={handleReset}
                    disabled={worksQuery.isFetching}
                    canAddCriteria
                />
            )}
            <div className="search-list">
                <div className="found-items-wrapper">
                    {hasSearched && (
                        <WorkResultsCardTable
                            works={createWorkSearchStore.createWorkSearchResults}
                            paginationInfo={createWorkSearchStore.createWorkSearchPaginationInfo}
                            limit={createWorkSearchStore.currentResultsPerPageLimit}
                            loading={worksQuery.isFetching}
                            compact
                            compactWorkComponentType={CatalogCompactWorkItem}
                            extendedWorkComponentType={CatalogExtendedWorkItem}
                            handleOnClick={handleClickCard}
                            displayType="public"
                            onChangePage={(newPagination) => {
                                createWorkSearchStore.setCurrentPage(newPagination.page ? newPagination.page : 1)
                            }}
                            onChangeLimit={(newLimit: number) => {
                                createWorkSearchStore.onChangeLimit(newLimit)
                            }}
                            outerDivClassName="individual-registration-work-results-table"
                        />
                    )}
                </div>
            </div>
        </div>
    )
}

export default observer(CreateWorkManualWorkSearch)
